import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (params.get("logout") === "1") {
      toast.success(t("farewell"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    // Trim to be forgiving of leading/trailing spaces (mobile keyboards)
    const r = await login(form.email.trim(), form.password.trim());
    setBusy(false);
    if (r.ok) {
      toast.success(t("welcomeBack"));
      navigate(location.state?.from || "/");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20 page-enter">
      <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">M16</div>
      <h1 className="font-display text-4xl mb-8">{t("signIn")}</h1>

      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input
          type="email" required value={form.email} dir="ltr" autoComplete="email" autoCapitalize="none" spellCheck={false}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder={t("email")} className="input-luxe" data-testid="login-email-input"
        />
        <div className="relative">
          <input
            type={showPw ? "text" : "password"} required value={form.password} dir="ltr" autoComplete="current-password" autoCapitalize="none" spellCheck={false}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={t("password")} className="input-luxe pr-11" data-testid="login-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? "hide password" : "show password"}
            data-testid="login-password-toggle"
            className="absolute inset-y-0 right-3 flex items-center text-[#9CA3AF] hover:text-[#D4AF37] transition"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button type="submit" disabled={busy} className="btn-gold w-full py-3 rounded-full" data-testid="login-submit-btn">
          {busy ? "..." : t("signIn")}
        </button>
      </form>

      <p className="text-sm text-[#9CA3AF] mt-6 text-center">
        {t("noAccount")}{" "}
        <Link to="/register" data-testid="goto-register-link" className="text-[#D4AF37] hover:underline">
          {t("register")}
        </Link>
      </p>
    </div>
  );
}
